/**
 * When the app itself fails, Felix opens on his own and says so (row f14fbbc8).
 *
 * Founder 6315077640 item 21: "when a real app-side error fires
 * (API/server/service/communication failure, after ruling out the user's own
 * connection), Felix pops up even with no active chat, tells the user the APP has
 * a problem (not them), proposes a ticket and runs ticket creation. User mistakes
 * (validation like min characters) get guidance only, no ticket."
 *
 * Before this, executeRequest handed every 4xx/5xx to reportErrorToFelix, which
 * only buffers it for the next chat turn: nothing opened, and nothing told the
 * user whose problem it was.
 *
 * HOW THE CONNECTION IS RULED OUT. A status code means a response came back, so
 * the user's connection reached FTL. A 5xx (including the 502/503/504 nginx
 * returns when a service behind it is down) is therefore the app's failure. A
 * request that never got a response throws inside fetch, before any status
 * exists, and never reaches classifyApiFailure: a lost connection does neither.
 *
 * Same shape as editConflict/scopeBlock: a narrowing function, a tiny store, and
 * one branch in the API client. FelixAppErrorNotice renders what this holds.
 */
import { create } from "zustand";
import { useFelixPanelStore } from "@/stores/felixPanel";

export type AppErrorKind = "app" | "user" | "ignore";

export interface ApiFailure {
  method: string;
  path: string;
  status: number;
  /** The human detail executeRequest already derived from the body. */
  detail: string;
}

/**
 * Paths whose failures are already shown where they happen: Felix's own chat
 * says when a turn fails, and a failed ticket filing must not propose a ticket
 * about itself.
 */
const SELF_REPORTING_PATHS = [/^\/assistant\//, /^\/feedback(\/|$)/, /^\/health/];

export function classifyApiFailure(f: ApiFailure): AppErrorKind {
  const path = f.path.split("?")[0];
  if (SELF_REPORTING_PATHS.some((re) => re.test(path))) return "ignore";
  if (f.status >= 500) return "app";
  // 400/422 on the user's own WRITE: what they entered was refused as it stands
  // (a field too short, a value out of range). Their mistake, so guidance and no
  // ticket. A 400 on a READ is not something the user did: measured on
  // production 2026-10-22, the page's background calendar read answers 400 "Not
  // connected to Google" and opened Felix telling the user to fix it.
  if ((f.status === 400 || f.status === 422) && f.method.toUpperCase() !== "GET") return "user";
  // 401 is the refresh flow's; 402/409/423 have their own dialogs; 403/404 keep
  // their existing per-page handling.
  return "ignore";
}

const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

/** The route with ids folded out, so one failing endpoint is one known issue
 *  however many records it failed on (the ticket dedup matches on the title). */
export function routeShape(path: string): string {
  return path.split("?")[0].replace(UUID_RE, ":id");
}

const WHAT_FAILED: Record<string, string> = {
  GET: "Loading this page's information",
  POST: "Creating that",
  PUT: "Saving your change",
  PATCH: "Saving your change",
  DELETE: "Deleting that",
};

/** The user-facing sentence for an app failure. Never the raw error: that goes
 *  only in the ticket (CLAUDE.md, "Felix must never show raw errors to users"). */
export function appFailureSentence(method: string): string {
  return `${WHAT_FAILED[method.toUpperCase()] ?? "That"} failed because of a problem on our side, not anything you did.`;
}

const GENERIC_GUIDANCE = "Check what you entered and try again.";

/** The guidance for a user mistake: the server's own sentence when it wrote one
 *  (a JSON blob is not a sentence), else a generic line. */
export function guidanceFrom(detail: string): string {
  const d = detail.trim();
  if (!d || d.startsWith("{") || d.startsWith("[") || d.length > 300) return GENERIC_GUIDANCE;
  return d;
}

export interface AppErrorNotice {
  kind: "app" | "user";
  /** What the user is told. */
  message: string;
  /** Ticket fields for an app failure (absent for a user mistake). */
  ticket?: { title: string; description: string; page_path: string };
  at: number;
  /** The request that failed, so the same request succeeding clears it. */
  method?: string;
  route?: string;
}

/** One notice per route and kind per minute: a page that polls a failing
 *  endpoint opens Felix once, not every few seconds. */
const REPEAT_WINDOW_MS = 60_000;

/** A notice older than this goes away at the next request that succeeds
 *  (row 321d0d14): on 10-25 at 16:02Z a deploy restart answered three polls
 *  with 502, and the notice still said "Loading this page's information
 *  failed" in a tab on /calendar the next morning, with the app long fine. */
export const NOTICE_TTL_MS = 10 * 60_000;

interface AppErrorNoticeState {
  notice: AppErrorNotice | null;
  lastShown: Record<string, number>;
  report: (f: ApiFailure) => void;
  /** A request succeeded: clear a notice about that same request, or any
   *  notice older than NOTICE_TTL_MS. The app answering again IS the
   *  recovery: a deploy's 502 is gone within seconds. */
  recovered: (method: string, path: string) => void;
  dismiss: () => void;
}

export const useAppErrorNoticeStore = create<AppErrorNoticeState>((set, get) => ({
  notice: null,
  lastShown: {},
  report: (f) => {
    const kind = classifyApiFailure(f);
    if (kind === "ignore") return;
    const now = Date.now();
    const route = routeShape(f.path);
    const key = `${kind} ${f.method} ${route}`;
    const { lastShown, notice } = get();
    if (now - (lastShown[key] ?? 0) < REPEAT_WINDOW_MS) return;
    // An app failure waiting for the user's answer is not replaced by guidance.
    if (notice?.kind === "app" && kind === "user") return;
    const page = typeof window !== "undefined" ? window.location.pathname : "";
    const next: AppErrorNotice =
      kind === "app"
        ? {
            kind,
            message: appFailureSentence(f.method),
            ticket: {
              title: `App error: ${f.method} ${route} returned ${f.status}`,
              description:
                `Felix opened on his own after this request failed on the server.\n\n` +
                `Request: ${f.method} ${f.path}\nStatus: ${f.status}\nPage: ${page}\n` +
                `Server said: ${f.detail.slice(0, 2000)}`,
              page_path: page,
            },
            at: now,
            method: f.method.toUpperCase(),
            route,
          }
        : { kind, message: guidanceFrom(f.detail), at: now };
    set({ notice: next, lastShown: { ...lastShown, [key]: now } });
    useFelixPanelStore.getState().setOpen(true);
  },
  recovered: (method, path) => {
    const { notice } = get();
    if (!notice) return;
    const same = notice.kind === "app" && notice.method === method.toUpperCase()
      && notice.route === routeShape(path);
    if (same || Date.now() - notice.at > NOTICE_TTL_MS) set({ notice: null });
  },
  dismiss: () => set({ notice: null }),
}));
